import React, { JSX } from 'react'
import { Chip } from '@mui/material'
import moment from 'moment'
import 'moment-timezone'
import BaseDisplayItem, { BaseDisplayItemProps } from '../../BaseDisplayItem'
import { DateFieldShow } from './DateFieldShow'
import {
    formatDateOnlyDisplay,
    formatDateTimeDisplay,
} from './format'
import { buildDateFieldInitialState, getSearchTypeTitle } from './search'
import {
    DateFieldExtendedState,
    DateFieldItem,
    DateFieldMode,
    DateFieldValue,
} from './types'

export abstract class BaseDateField extends BaseDisplayItem<
    DateFieldValue,
    DateFieldItem,
    HTMLDivElement,
    DateFieldExtendedState
> {
    protected readonly fieldMode: DateFieldMode

    protected abstract serializeValue(m: moment.Moment): string

    constructor(
        props: BaseDisplayItemProps<DateFieldValue, DateFieldItem>,
        fieldMode: DateFieldMode
    ) {
        super(props)
        this.kedo = props.kedo
        this.fieldMode = fieldMode

        this.state = buildDateFieldInitialState(props, fieldMode)

        this.onChangeSearch = this.onChangeSearch.bind(this)
        this.onChangeDate = this.onChangeDate.bind(this)
        this.getFormattedDate = this.getFormattedDate.bind(this)
        this.isInvalid = this.isInvalid.bind(this)
    }

    protected changeValueForSearch(searchValue: {
        custom: string
        type: string
        date1: string | null
        date2: string | null
    }) {
        const dossierData = this.props.item.settings?.dossier_data
        if (dossierData === 'created_at' || dossierData === 'modified_at') {
            this.props.changeValue?.(dossierData, searchValue)
        } else {
            this.props.changeValue?.(this.props.item.id, searchValue)
        }
    }

    protected changeValueForEdit(value: string | null) {
        const dossierData = this.props.item.settings?.dossier_data
        if (dossierData === 'created_at' || dossierData === 'modified_at') {
            this.props.changeValue?.(dossierData, value)
        } else {
            this.props.changeValue?.(this.props.item.id, value)
        }
    }

    onChangeSearch(searchType: string) {
        const searchValue = {
            custom: 'date',
            type: searchType,
            date1: this.state.date1,
            date2: this.state.date2,
        }

        this.setState({ searchType })
        this.changeValueForSearch(searchValue)
    }

    onChangeDate(
        m: moment.Moment | null,
        name?: string,
        searchType?: string
    ) {
        const isSearchMode =
            this.props.mode === 'search' ||
            this.props.mode === 'advanced-search'

        if (!m || !m.format) {
            if (!isSearchMode) {
                this.setState({ value: null })
                this.changeValueForEdit(null)
            }
            return
        }

        const serialized = this.serializeValue(m)

        if (isSearchMode) {
            const searchValue = {
                custom: 'date',
                type: this.state.searchType,
                date1: this.state.date1,
                date2: this.state.date2,
            }

            if (name === 'date1') {
                searchValue.date1 = serialized
                this.setState({ date1: serialized })
            } else if (name === 'date2') {
                searchValue.date2 = serialized
                this.setState({ date2: serialized })
            } else if (searchType) {
                searchValue.type = searchType
                this.setState({ searchType })
            } else {
                this.setState({ value: serialized })
            }

            this.changeValueForSearch(searchValue)
            return
        }

        this.setState({ value: serialized })
        this.changeValueForEdit(serialized)
    }

    getFormattedDate(value: string): string | null {
        return this.fieldMode === 'datetime'
            ? formatDateTimeDisplay(value)
            : formatDateOnlyDisplay(value)
    }

    getSearchTypeTitle(searchType: string): string {
        return getSearchTypeTitle(this.kedo, searchType)
    }

    getCurrentLocale(): string {
        const locale = this.kedo.env().getSelectedLocale()
        if (locale && locale.code) {
            return locale.code.replace('_', '-')
        }

        return 'en'
    }

    getColor(
        backgroundColor: string,
        text: string,
        fontSize = '90%'
    ): JSX.Element {
        return (
            <Chip
                label={text}
                size="small"
                sx={{
                    backgroundColor: backgroundColor,
                    color: '#fff',
                    fontSize: fontSize,
                    borderRadius: '16px',
                    height: 'auto',
                    px: 1.5,
                }}
            />
        )
    }

    renderShow(): React.ReactNode {
        return (
            <DateFieldShow
                kedo={this.kedo}
                value={this.state.value as string}
                settings={this.props.item.settings}
                getFormattedDate={this.getFormattedDate}
                deadline={this.state.deadline}
                getColor={this.getColor}
            />
        )
    }

    shouldComponentUpdate(
        nextProps: BaseDisplayItemProps<DateFieldValue, DateFieldItem>,
        nextState: DateFieldExtendedState
    ) {
        if (
            this.state.searchType !== nextState.searchType ||
            this.state.date1 !== nextState.date1 ||
            this.state.date2 !== nextState.date2 ||
            this.state.value !== nextState.value
        ) {
            return true
        }
        if (
            nextProps.mode !== this.props.mode ||
            nextProps.errors !== this.props.errors
        ) {
            return true
        }

        return false
    }
}
